import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/state/store/auth';
import { useGuestSessionStore } from '@/state/store/guestSession';
import { getPaymentStatus } from '@/modules/templates/services/guest-payment.service';

// Hook for the review page paywall. For authenticated users it short-circuits
// to { isPaid: true } so the existing org flow is unaffected. For guests it
// calls the backend's payment status endpoint, with optional polling
// (refreshKey) for the post-pay confirmation window.
//
// documentId comes from the `?document=...` URL param set by useAutoSaveDraft
// when the document was created. If missing, returns { isPaid: false } —
// guest hasn't autosaved yet.

export interface PaymentStatus {
  isPaid: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  documentId: string | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useGuestPaymentStatus = (
  documentId: string | null,
): PaymentStatus => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const guestSessionId = useGuestSessionStore((s) => s.guestSessionId);
  const isGuest = !isAuthenticated && !!guestSessionId;

  const [isPaid, setIsPaid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (isAuthenticated) {
      // Auth users bypass the paywall entirely.
      setIsPaid(true);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (!isGuest || !documentId) {
      setIsPaid(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    getPaymentStatus(documentId)
      .then((res) => {
        if (cancelledRef.current) return;
        setIsPaid(!!res.isPaid);
      })
      .catch((err: unknown) => {
        if (cancelledRef.current) return;
        setError(err instanceof Error ? err.message : 'Payment status check failed');
      })
      .finally(() => {
        if (!cancelledRef.current) setIsLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [isAuthenticated, isGuest, documentId, tick]);

  return {
    isPaid,
    isAuthenticated,
    isGuest,
    documentId,
    isLoading,
    error,
    refresh: () => setTick((n) => n + 1),
  };
};
